import * as React from 'react';
import { AgeGroupFormats, SeasonGuideContext } from './season-guide';
import { Select, Field } from '@teamsnap/teamsnap-ui';
import './age-range.scss';

const buildAgeGroups = (season, selectedRange): string[] => {
  const options = [];
  if (season.ageGroupTitle == AgeGroupFormats.AGE_AND_UNDER) {
    for (let i = +selectedRange[0]; i <= selectedRange[1]; i++) {
      options.push(`${i}U`);
    }
  } else if (season.ageGroupTitle == AgeGroupFormats.UNDER) {
    for (let i = +selectedRange[0]; i <= +selectedRange[1]; i++) {
      options.push(`U${i}`);
    }
  } else if (season.ageGroupTitle == AgeGroupFormats.BIRTHYEAR) {
    for (let i = +selectedRange[0]; i <= +selectedRange[1]; i++) {
      options.push(`${i}`);
    }
  } else if (season.ageGroupTitle == AgeGroupFormats.SCHOOL_GRADE) {
    const fullGradeOptions = gradeOptionsList();
    fullGradeOptions.slice(+selectedRange[0], +selectedRange[1] + 1).forEach((item) => {
      options.push(item.label);
    });
  } else if (season.ageGroupTitle == AgeGroupFormats.GRADUATION_YEAR) {
    for (let i = +selectedRange[0]; i <= +selectedRange[1]; i++) {
      options.push(`${i}`);
    }
  } else if (season.ageGroupTitle == AgeGroupFormats.OTHER) {
    for (let i = 0; i < +selectedRange.length; i++) {
      options.push(`${selectedRange[i].trim()}`);
    }
  }
  return options;
};

const ageOptionsList = () => {
  const ageMin = 5;
  const ageMax = 23;
  const options = [];

  for (let i = ageMin; i <= ageMax; i++) {
    options.push({
      label: i.toString(),
      value: i.toString(),
      disabled: false,
    });
  }

  return options;
};

const birthyearOptionsList = () => {
  const currentYear = +new Date().getFullYear();
  const amount = 23;
  const options = [];

  for (let i = 0; i <= amount; i++) {
    options.push({
      label: (currentYear - i).toString(),
      value: currentYear - i,
      disabled: false,
    });
  }

  return options.reverse();
};

const gradeOptionsList = () => [
  {
    label: 'K',
    value: 0,
  },
  {
    label: '1st Grade',
    value: 1,
  },
  {
    label: '2nd Grade',
    value: 2,
  },
  {
    label: '3rd Grade',
    value: 3,
  },
  {
    label: '4th Grade',
    value: 4,
  },
  {
    label: '5th Grade',
    value: 5,
  },
  {
    label: '6th Grade',
    value: 6,
  },
  {
    label: '7th Grade',
    value: 7,
  },
  {
    label: '8th Grade',
    value: 8,
  },
  {
    label: '9th Grade',
    value: 9,
  },
  {
    label: '10th Grade',
    value: 10,
  },
  {
    label: '11th Grade',
    value: 11,
  },
  {
    label: '12th Grade',
    value: 12,
  },
];

const graduationOptionsList = () => {
  const currentYear = +new Date().getFullYear();
  const amount = 23;
  const options = [];

  for (let i = 0; i <= amount; i++) {
    options.push({
      label: (currentYear + i).toString(),
      value: currentYear + i,
      disabled: false,
    });
  }

  return options;
};

const getOptionsList = (selectedAgeGroup) => {
  if (selectedAgeGroup == AgeGroupFormats.AGE_AND_UNDER || selectedAgeGroup == AgeGroupFormats.UNDER) {
    return ageOptionsList();
  } else if (selectedAgeGroup == AgeGroupFormats.BIRTHYEAR) {
    return birthyearOptionsList();
  } else if (selectedAgeGroup == AgeGroupFormats.SCHOOL_GRADE) {
    return gradeOptionsList();
  } else if (selectedAgeGroup == AgeGroupFormats.GRADUATION_YEAR) {
    return graduationOptionsList();
  } else {
    return [];
  }
};

const AgeRange = () => {
  const context = React.useContext(SeasonGuideContext);
  const [rangeStartOptionsList, setRangeStartOptionsList] = React.useState([]);
  const [rangeEndOptionsList, setRangeEndOptionsList] = React.useState([]);
  const [otherRange, setOtherRange] = React.useState('');

  React.useEffect(() => {
    if (!context.season.ageGroupTitle) return;

    if (context.season.ageGroupTitle != AgeGroupFormats.OTHER) {
      const optionList = getOptionsList(context.season.ageGroupTitle);
      setRangeStartOptionsList(optionList);
      setRangeEndOptionsList(optionList);
      setOtherRange('');

      context.setSeason({
        ageRanges: [optionList[0]?.value, optionList[1]?.value],
        ageRangeOther: null,
      });
    } else if (context.season.ageGroupTitle == AgeGroupFormats.OTHER && context.season.ageRangeOther?.length > 0) {
      setOtherRange(context.season.ageRangeOther.join(', '));
    } else {
      context.setSeason({
        ageGroupsOptions: buildAgeGroups(context.season, []),
        ageRanges: null,
        ageRangeOther: null,
      });
    }
  }, [context.season.ageGroupTitle]);

  React.useEffect(() => {
    if (context.season.ageRanges?.length > 0) {
      context.setSeason({
        ageGroupsOptions: buildAgeGroups(context.season, context.season.ageRanges),
        ageRangeOther: null,
      });
    }
  }, [context.season.ageRanges]);

  React.useEffect(() => {
    if (otherRange && context.season.ageGroupTitle == AgeGroupFormats.OTHER) {
      context.setSeason({
        ageGroupsOptions: buildAgeGroups(context.season, otherRange.split(',')),
        ageRanges: null,
        ageRangeOther: otherRange.split(','),
      });
    }
  }, [otherRange]);

  return (
    <>
      <h2 className="u-spaceBottomLg">How do you format your age group title? *</h2>
      <div className="group-title-buttons">
        <button
          data-testid={`group-title-button-${AgeGroupFormats.AGE_AND_UNDER}`}
          className={`group-title-button ${
            context.season.ageGroupTitle == AgeGroupFormats.AGE_AND_UNDER ? 'current' : ''
          }`}
          onClick={() => context.setSeason({ ageGroupTitle: AgeGroupFormats.AGE_AND_UNDER })}
          type="button"
        >
          <strong>14 U</strong>
          <span>{AgeGroupFormats.AGE_AND_UNDER}</span>
        </button>
        <button
          data-testid={`group-title-button-${AgeGroupFormats.UNDER}`}
          className={`group-title-button ${context.season.ageGroupTitle == AgeGroupFormats.UNDER ? 'current' : ''}`}
          onClick={() => context.setSeason({ ageGroupTitle: AgeGroupFormats.UNDER })}
          type="button"
        >
          <strong>U14</strong>
          <span>{AgeGroupFormats.UNDER}</span>
        </button>
        <button
          data-testid={`group-title-button-${AgeGroupFormats.BIRTHYEAR}`}
          className={`group-title-button ${context.season.ageGroupTitle == AgeGroupFormats.BIRTHYEAR ? 'current' : ''}`}
          onClick={() => context.setSeason({ ageGroupTitle: AgeGroupFormats.BIRTHYEAR })}
          type="button"
        >
          <strong>{new Date().getFullYear() - 23}</strong>
          <span>{AgeGroupFormats.BIRTHYEAR}</span>
        </button>
        <button
          data-testid={`group-title-button-${AgeGroupFormats.SCHOOL_GRADE}`}
          className={`group-title-button ${
            context.season.ageGroupTitle == AgeGroupFormats.SCHOOL_GRADE ? 'current' : ''
          }`}
          onClick={() => context.setSeason({ ageGroupTitle: AgeGroupFormats.SCHOOL_GRADE })}
          type="button"
        >
          <strong>8th</strong>
          <span>{AgeGroupFormats.SCHOOL_GRADE}</span>
        </button>
        <button
          data-testid={`group-title-button-${AgeGroupFormats.GRADUATION_YEAR}`}
          className={`group-title-button ${
            context.season.ageGroupTitle == AgeGroupFormats.GRADUATION_YEAR ? 'current' : ''
          }`}
          onClick={() => context.setSeason({ ageGroupTitle: AgeGroupFormats.GRADUATION_YEAR })}
          type="button"
        >
          <strong>{new Date().getFullYear() + 23}</strong>
          <span>{AgeGroupFormats.GRADUATION_YEAR}</span>
        </button>
        <button
          data-testid={`group-title-button-${AgeGroupFormats.OTHER}`}
          className={`group-title-button ${context.season.ageGroupTitle == AgeGroupFormats.OTHER ? 'current' : ''}`}
          onClick={() => context.setSeason({ ageGroupTitle: AgeGroupFormats.OTHER })}
          type="button"
        >
          <strong>...</strong>
          <span>{AgeGroupFormats.OTHER}</span>
        </button>
      </div>

      {(context.season.ageGroupTitle == AgeGroupFormats.AGE_AND_UNDER ||
        context.season.ageGroupTitle == AgeGroupFormats.UNDER) && (
        <h2 className="u-spaceBottomLg">What is the age range for this season? *</h2>
      )}
      {context.season.ageGroupTitle == AgeGroupFormats.BIRTHYEAR && (
        <h2 className="u-spaceBottomLg">What is the birth year range for this season? *</h2>
      )}
      {context.season.ageGroupTitle === AgeGroupFormats.SCHOOL_GRADE && (
        <h2 className="u-spaceBottomLg">What is the school grade range for this season? *</h2>
      )}
      {context.season.ageGroupTitle == AgeGroupFormats.GRADUATION_YEAR && (
        <h2 className="u-spaceBottomLg">What is the graduation year range for this season? *</h2>
      )}
      {context.season.ageGroupTitle == AgeGroupFormats.OTHER && (
        <>
          <h2 className="u-spaceBottomSm">What are your program's age group titles? *</h2>
          <h3 className="u-spaceBottomLg u-colorNeutral7">Seperate titles by comma.</h3>
        </>
      )}
      <div className="age-range-container u-displayFlex">
        {context.season.ageGroupTitle && context.season.ageGroupTitle != AgeGroupFormats.OTHER && (
          <>
            <Select
              data-testid="age-group-range-start"
              mods="u-spaceRightMd"
              name="standardSelect"
              className="SelectBox SelectBox--xlarge"
              options={rangeStartOptionsList}
              inputProps={{
                value: context.season.ageRanges ? context.season.ageRanges[0] : null,
                onChange: (e) => {
                  context.setSeason({ ageRanges: [e.target.value, context.season.ageRanges[1]] });
                },
              }}
            />
            <span>To</span>
            <Select
              data-testid="age-group-range-end"
              mods="u-spaceLeftMd"
              name="standardSelect"
              className="SelectBox SelectBox--xlarge"
              options={rangeEndOptionsList}
              inputProps={{
                value: context.season.ageRanges ? context.season.ageRanges[1] : null,
                onChange: (e) => {
                  context.setSeason({ ageRanges: [context.season.ageRanges[0], e.target.value] });
                },
              }}
            />
          </>
        )}
        {context.season.ageGroupTitle == AgeGroupFormats.OTHER && (
          <Field
            data-testid="age-group-other"
            type="text"
            name="other"
            formFieldProps={{
              size: 'large',
              placeholder: 'Example: 12U, 14U/8th Grade, 9th Grade',
              inputProps: {
                value: otherRange,
                onChange: (e) => {
                  setOtherRange(e.target.value);
                  context.setSeason({ ageRangeOther: e.target.value.split(',') });
                },
              },
            }}
          />
        )}
      </div>
    </>
  );
};

export default AgeRange;
